import React, { useRef, useState } from 'react';
import Barcode from 'react-barcode';
import Modal from './Modal';
import Button from '../forms/Button';
import Select from '../forms/Select';

interface BarcodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  serialNumber: string;
  productName?: string;
}

type LabelSize = '4x6' | '4x2' | '2x1';

const labelSizes = {
  '4x6': { width: '101.6mm', height: '152.4mm', name: '4x6 inch (Büyük)' },
  '4x2': { width: '101.6mm', height: '50.8mm', name: '4x2 inch (Orta)' },
  '2x1': { width: '50.8mm', height: '25.4mm', name: '2x1 inch (Küçük)' },
};

const BarcodeModal: React.FC<BarcodeModalProps> = ({
  isOpen,
  onClose,
  serialNumber,
  productName,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [labelSize, setLabelSize] = useState<LabelSize>('4x2');

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const size = labelSizes[labelSize];
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Barkod Yazdır - ${serialNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }
            .barcode-container {
              width: ${size.width};
              height: ${size.height};
              padding: 3mm;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
              page-break-after: always;
            }
            .product-name {
              font-size: ${labelSize === '2x1' ? '7pt' : '9pt'};
              font-weight: bold;
              margin-bottom: 2mm;
              color: #000;
              max-width: 95%;
              overflow: hidden;
              text-overflow: ellipsis;
              ${labelSize === '2x1' ? 'white-space: nowrap;' : ''}
            }
            svg {
              max-width: 95%;
            }
            @media print {
              @page {
                size: ${size.width} ${size.height};
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const size = labelSizes[labelSize];
  const barcodeWidth = labelSize === '2x1' ? 1.5 : 2;
  const barcodeHeight = labelSize === '2x1' ? 30 : labelSize === '4x6' ? 70 : 50;
  const fontSize = labelSize === '2x1' ? 10 : 12;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Barkod Yazdır" size="md">
      <div className="mb-4">
        <Select
          label="Etiket Boyutu"
          value={labelSize}
          onChange={(value) => setLabelSize(value as LabelSize)}
          options={[
            { value: '4x6', label: labelSizes['4x6'].name },
            { value: '4x2', label: labelSizes['4x2'].name },
            { value: '2x1', label: labelSizes['2x1'].name },
          ]}
        />
      </div>
      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 mb-4">
        <div ref={printRef} className="barcode-container bg-white py-6">
          {productName && (
            <div className="product-name text-sm font-semibold mb-2 text-gray-800 text-center px-2">
              {productName}
            </div>
          )}
          <div className="flex justify-center">
            <Barcode
              value={serialNumber}
              format="CODE128"
              width={barcodeWidth}
              height={barcodeHeight}
              displayValue={true}
              fontSize={fontSize}
              margin={3}
            />
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-600 mb-4 p-3 bg-blue-50 rounded border border-blue-200">
        <strong>Not:</strong> Yazdırma dialogunda kağıt boyutunu manuel olarak seçmeniz gerekebilir.
        Seçili etiket boyutu: <strong>{size.name}</strong> ({size.width} x {size.height})
      </div>
      <div className="flex gap-2">
        <Button onClick={handlePrint} fullWidth>
          Yazdır
        </Button>
        <Button variant="outline" onClick={onClose} fullWidth>
          Kapat
        </Button>
      </div>
    </Modal>
  );
};

export default BarcodeModal;
